<script setup>
import { inject, ref, onMounted, watch, computed } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import API from '@/js/api'
import Calendar from './components/Calendar.vue'

const api = inject('api')
const { t } = useI18n()
const isLoading = ref(false)
const grid = ref(null)
const data = ref(null)
const activeModal = ref({ filters: false, calendar: false })
const isMobile = useMediaQuery('(max-width: 767px)')
const hasFiltersData = ref(false)

const hasFilters = computed(() =>
  Object.entries(model.value).some(([k, v]) => k != 'dates' && v.length),
)

const hasAnyFilters = computed(() =>
  Object.entries(model.value).some(([k, v]) => v.length),
)

const filters = ref(null)
const model = ref({})
const modelTime = ref(api.time ? 'future' : null)

const getData = async () => {
  const qs =
    '?' +
    new URLSearchParams(
      Object.fromEntries(
        Object.entries(model.value).filter(([key, value]) => value.length),
      ),
    )

  window.history.replaceState(null, {}, qs)

  isLoading.value = true
  if (api.time) {
    data.value = {
      past: await API.get(api.index + 'past/' + qs),
      future: await API.get(api.index + 'future/' + qs),
    }

    grid.value = data.value[modelTime.value].html
  } else {
    data.value = await API.get(api.index + qs)
    grid.value = data.value.html
  }

  isLoading.value = false
}

const resetFilter = (key) => {
  if (key === true) {
    model.value = Object.fromEntries(
      Object.entries(model.value).map(([k]) => [k, []]),
    )
  } else if (key) model.value[key] = Array.isArray(model.value[key]) ? [] : null
  else
    model.value = Object.fromEntries(
      Object.entries(model.value).map(([k, v]) =>
        k != 'dates' ? [k, []] : [k, v],
      ),
    )
}

onMounted(async () => {
  if (api.filters) {
    isLoading.value = true
    filters.value = await API.get(api.filters)
    const qs = new URLSearchParams(window.location.search)
    isLoading.value = false

    filters.value.forEach((e) => {
      const q = qs.get(e.key)
      model.value[e.key] = q ? q.split(',') : []
      if (!hasFiltersData.value && e.values?.length > 1)
        hasFiltersData.value = true
    })

    model.value.dates = qs.get('dates') ? qs.get('dates').split(',') : []
  }

  if (api.index) {
    await getData()
  }

  watch(
    model,
    () => {
      getData()
    },
    { deep: true },
  )

  watch(modelTime, () => {
    grid.value = data.value[modelTime.value].html
  })

  if (data.value?.future?.count == 0) {
    modelTime.value = 'past'
  }
})
</script>

<template>
  <div class="">
    <div id="vue-index-filters" class="is-relative mb-4">
      <b-loading :is-full-page="true" v-model="isLoading" />

      <div class="columns is-multiline is-variable is-2">
        <div class="column is-narrow filter-container mr-auto" v-if="!isMobile">
          <template v-for="filter in filters" :key="'filter-' + filter.key">
            <b-dropdown
              aria-role="list"
              :mobile-modal="false"
              v-if="filter.values?.length > 1"
            >
              <template #trigger>
                <button
                  type="button"
                  class="button is-lighter is-large is-size-6 is-fullwidth"
                >
                  <span>{{ filter.label }}</span>
                  <b-icon icon="caret-down-bold" class="ml-2" />
                  <p
                    v-if="model[filter.key].length"
                    class="button is-xs is-black is-rounded is-square ml-4"
                    @click.stop="resetFilter(filter.key)"
                  >
                    <b-icon icon="x" />
                  </p>
                </button>
              </template>

              <b-dropdown-item
                custom
                aria-role="listitem"
                v-for="(item, index) in filter.values"
                :key="'items-' + index"
              >
                <b-checkbox
                  v-model="model[filter.key]"
                  :native-value="item._id"
                >
                  {{ item.name }}
                </b-checkbox>
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <Calendar v-model="model.dates" v-if="api.time" />
        </div>

        <template v-else>
          <div class="column" v-if="hasFiltersData">
            <button
              type="button"
              class="button is-large is-lighter has-radius-control is-size-6 is-fullwidth"
              @click="activeModal.filters = true"
            >
              <div class="is-flex is-align-items-center">
                <span v-text="t('index.filters')" />
                <p
                  v-if="hasFilters"
                  class="button is-xs is-black is-rounded is-square ml-3"
                  @click.stop="resetFilter()"
                >
                  <b-icon icon="x" />
                </p>
              </div>
              <div
                class="button is-rounded is-square is-bordered is-disabled ml-2"
              >
                <b-icon icon="arrow-up-right" class="is-size-5" />
              </div>
            </button>

            <b-modal
              v-model="activeModal.filters"
              full-screen
              :can-cancel="['outside']"
            >
              <div class="columns is-vcentered is-mobile">
                <div class="column">
                  <b-button
                    type="is-white"
                    :icon-right="hasFilters ? 'x-circle-light' : null"
                    @click="resetFilter()"
                  >
                    {{ t('index.filters') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button
                    icon-left="x"
                    class="is-white"
                    @click="activeModal.filters = false"
                  ></b-button>
                </div>
              </div>

              <div class="filter-container">
                <template
                  v-for="filter in filters"
                  :key="'filter-' + filter.key"
                >
                  <template v-if="filter.values?.length > 1">
                    <b-button
                      type="is-lighter"
                      :icon-right="
                        model[filter.key].length ? 'x-circle-fill' : null
                      "
                      expanded
                      @click.stop="resetFilter(filter.key)"
                    >
                      {{ filter.label }}
                    </b-button>

                    <div class="filter-list">
                      <b-checkbox
                        v-for="(item, index) in filter.values"
                        :key="'items-' + index"
                        v-model="model[filter.key]"
                        :native-value="item._id"
                      >
                        {{ item.name }}
                      </b-checkbox>
                    </div>
                  </template>
                </template>
              </div>

              <b-button
                type="is-primary"
                class="has-radius-small"
                expanded
                @click="activeModal.filters = false"
              >
                {{ t('index.apply') }}
              </b-button>
            </b-modal>
          </div>

          <div class="column" v-if="api.time">
            <button
              type="button"
              class="button is-large is-lighter has-radius-control is-size-6 is-fullwidth"
              @click="activeModal.calendar = true"
            >
              <div class="is-flex is-align-items-center">
                <span v-text="t('index.calendar')" />
                <p
                  v-if="model.dates?.length"
                  class="button is-xs is-black is-rounded is-square ml-3"
                  @click.stop="resetFilter('dates')"
                >
                  <b-icon icon="x" />
                </p>
              </div>

              <div
                class="button is-rounded is-square is-bordered is-disabled ml-2"
              >
                <b-icon icon="arrow-up-right" class="is-size-5" />
              </div>
            </button>

            <b-modal
              v-model="activeModal.calendar"
              full-screen
              :can-cancel="['outside']"
            >
              <div class="columns is-vcentered is-mobile">
                <div class="column">
                  <b-button
                    type="is-white"
                    :icon-right="model.dates.length ? 'x-circle-light' : null"
                    @click="resetFilter('dates')"
                  >
                    {{ t('index.calendar') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button
                    icon-left="x"
                    class="is-white"
                    @click="activeModal.calendar = false"
                  ></b-button>
                </div>
              </div>

              <Calendar
                v-model="model.dates"
                inline
                @close="activeModal.calendar = false"
              />
            </b-modal>
          </div>
        </template>

        <div class="column is-narrow" v-if="api.time && data?.future?.count">
          <b-field grouped v-if="grid">
            <b-radio-button
              v-model="modelTime"
              native-value="future"
              type="is-primary"
              expanded
            >
              <span class="tag">{{ data?.future.count }}</span>
              <span>{{ t('index.future') }}</span>
            </b-radio-button>

            <b-radio-button
              v-model="modelTime"
              native-value="past"
              type="is-primary"
              expanded
            >
              <span class="tag">{{ data?.past.count }}</span>
              <span>{{ t('index.past') }}</span>
            </b-radio-button>
          </b-field>
        </div>
      </div>
    </div>

    <div
      id="vue-index-grid"
      class="columns has-fullheight-box is-multiline"
      v-if="grid"
      v-html="grid"
    ></div>

    <div
      class="has-text-centered"
      v-if="
        data &&
        hasAnyFilters &&
        ((!modelTime && !data.count) || (modelTime && !data[modelTime].count))
      "
    >
      <b-button
        type="is-primary"
        class="is-medium is-size-6 mb-6"
        @click="resetFilter(true)"
        rounded
        >{{ t('index.reset') }}</b-button
      >
    </div>
  </div>
</template>
